import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import AuthService from '../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useBranch } from './BranchContext';
import config, { isDemoHost } from '../config/api.config';
import { isDeskAdmin } from '../utils/authHelpers';

interface UserInfo {
  id: string;
  email: string;
  name: string;
  mobile?: string;
  branch_id: string;
  permissions?: string[];
}

interface BranchLicenses {
  branchId: number;
  club: boolean;
  evaluation: boolean;
  /** Derived: club OR evaluation. Kept for API compatibility / Market Pulse access. */
  marketPulse: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  licenses: BranchLicenses | null;
  login: (magicToken: string, brandId?: string, branchId?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => void;
  fetchLicenses: (branchId: string | number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}



export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [licenses, setLicenses] = useState<BranchLicenses | null>(null);
  const [hasInitiatedRedirect, setHasInitiatedRedirect] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // const { selectedBranch } = useBranch();
  

  const fetchLicenses = useCallback(async (branchId: string | number) => {
    try {
      // Convert to number if it's a string
      const numericBranchId = typeof branchId === 'string' ? parseInt(branchId, 10) : branchId;
      let user_info = JSON.parse(localStorage.getItem("insight_user_info"))
      const userId = user_info.id;
      console.log(userId)
      // Skip if branchId is invalid
      if (isNaN(numericBranchId)) {
        logger.warn(`Invalid branch ID for license fetch: ${branchId}`, 'AuthContext');
        setLicenses(null);
        return;
      }
      
      const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
      const isDevelopment = config.STAGE_NO_AUTH || config.ENV === 'development';
      
      const response = await fetch(`${baseUrl}/api/branch-licenses?branch_id=${numericBranchId}&user_id=${userId}&development=${isDevelopment}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const payload = data.data || {};
        const club = Boolean(payload.club);
        const evaluation = Boolean(payload.evaluation);
        setLicenses({
          branchId: payload.branchId ?? numericBranchId,
          club,
          evaluation,
          marketPulse: club || evaluation,
        });
      } else {
        logger.error(`Failed to fetch licenses: ${response.status} ${response.statusText}`, 'AuthContext');
        setLicenses(null);
      }
    } catch (error) {
      logger.error('Failed to fetch licenses', 'AuthContext', error);
      setLicenses(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = AuthService.getToken();
    let userInfo = AuthService.getUserInfo();
    
    logger.debug('checkAuth called', 'AuthContext', { 
      hasToken: !!token,
      hasUserInfo: !!userInfo,
      userInfo 
    });
    
    if (token) {
      // If we have a token but no user info, fetch it from the API
      if (!userInfo) {
        try {
          logger.info('Token exists but no user info cached, fetching from API...', 'AuthContext');
          const response = await AuthService.getCurrentUser();
          logger.debug('getCurrentUser response', 'AuthContext', response);
          if (response) {
            userInfo = response;
            console.log(response)
            AuthService.saveUserInfo(userInfo);
          }
        } catch (error: any) {
          logger.error('Failed to fetch current user info', 'AuthContext', error);
          // Only clear auth if it's a 401 error, not for network issues
          if (error.response?.status === 401) {
            AuthService.clearAuth();
            setIsAuthenticated(false);
            setUser(null);
            setLicenses(null);
            setIsLoading(false);
            return;
          } else {
            // For other errors, keep the existing auth state
            logger.warn('Keeping existing auth state despite error', 'AuthContext');
          }
        }
      }
      
      if (userInfo) {
        setIsAuthenticated(true);
        setUser(userInfo);
        setHasInitiatedRedirect(false); // Reset redirect flag on successful auth

        if (!config.STAGE_NO_AUTH && config.isStagingHost() && location.pathname !== '/auth/env-handoff') {
          try {
            const brandId = AuthService.getSavedBrandId() ?? undefined;
            const branchId = AuthService.getSavedBranchId() ?? undefined;
            const deskUser = await AuthService.fetchUserInfo(brandId, branchId);
            if (!isDeskAdmin(deskUser)) {
              logger.warn('Non-admin session on staging — clearing auth', 'AuthContext');
              AuthService.clearAuth();
              setIsAuthenticated(false);
              setUser(null);
              setLicenses(null);
              setIsLoading(false);
              window.location.replace(config.INSIGHT_PROD_URL);
              return;
            }
          } catch (stagingGateError) {
            logger.error('Staging admin gate failed', 'AuthContext', stagingGateError);
            AuthService.clearAuth();
            setIsAuthenticated(false);
            setUser(null);
            setLicenses(null);
            setIsLoading(false);
            window.location.replace(config.INSIGHT_PROD_URL);
            return;
          }
        }
        // Don't fetch licenses here - wait for branch selection
      } else if (!userInfo) {
        // Only set as not authenticated if we couldn't get user info AND got a 401
        logger.warn('Token exists but no user info available', 'AuthContext');
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setLicenses(null);
    }
    setIsLoading(false);
  }, [location.pathname]);

  const setStageMockUser = () => {
    const mockUser = {
      id: 'prod-test-user',
      email: 'test@smartx.ir',
      name: 'Test User',
      branch_id: '4160',
      permissions: ['admin', 'read', 'write', 'delete']
    };

    localStorage.setItem('insight_auth_token', 'mock-token-for-testing');
    localStorage.setItem('insight_refresh_token', 'mock-refresh-token');
    localStorage.setItem('insight_user_info', JSON.stringify(mockUser));

    setIsAuthenticated(true);
    setUser(mockUser);
    setIsLoading(false);
  };

  const handleDevLogin = useCallback(async () => {
    try {
      setIsLoading(true);
      await AuthService.devLogin();
      await checkAuth();
    } catch (error) {
      logger.error('Failed to login with dev credentials', 'AuthContext', error);
      setIsLoading(false);
    }
  }, [checkAuth]);

  const handleMagicToken = useCallback(async (magicToken: string, brandId?: string, branchId?: string) => {
    try {
      setIsLoading(true);
      logger.info('Starting magic token validation', 'AuthContext', { brandId, branchId });
      
      const response = await AuthService.validateMagicToken(magicToken, brandId, branchId);
      logger.info('Magic token validated successfully', 'AuthContext');
      
      await checkAuth();
      logger.info('Auth check completed after magic token', 'AuthContext', { 
        userMobile: user?.mobile 
      });

      const userInfo = await AuthService.fetchUserInfo(
            +brandId,
            +branchId
          );
      logger.info('User info fetched with mobile', 'AuthContext', { 
        mobile: userInfo.mobile,
        userFullName: userInfo.userFullName 
      });
      
      // Update user state with the fetched mobile number
      const updatedUser = AuthService.getUserInfo();
      logger.info('Retrieved updated user from localStorage', 'AuthContext', { 
        mobile: updatedUser?.mobile,
        name: updatedUser?.name,
        email: updatedUser?.email
      });
      
      if (updatedUser) {
        setUser(updatedUser);
        logger.info('User state updated with mobile number', 'AuthContext', { 
          mobile: updatedUser.mobile 
        });
      } else {
        logger.warn('No updated user info found in localStorage', 'AuthContext');
      }
      
      // Navigate to the redirect URL from backend (main dashboard)
      const redirectUrl = response.redirect_url || '/dashboard/main';
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      logger.error('Failed to validate magic token', 'AuthContext', error);
      if (config.STAGE_NO_AUTH) {
        if (!user) {
          setStageMockUser();
        } else {
          setIsLoading(false);
        }
        navigate('/dashboard/main', { replace: true });
        return;
      }

      AuthService.redirectToDesk();
    }
  }, [navigate, checkAuth, user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Skip this effect if we're on an auth callback route
    if (
      location.pathname === '/auth/validate-magic-token' ||
      location.pathname === '/auth/env-handoff' ||
      location.pathname === '/demo-login'
    ) {
      return;
    }
    
    // Only process authentication logic after loading is complete
    if (!isLoading && !isAuthenticated) {
      // Prevent multiple redirects
      if (hasInitiatedRedirect) {
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      // Handle both 'token' and 'magic_token' parameters for compatibility
      const magicToken = searchParams.get('token') || searchParams.get('magic_token');
      const refreshKey =
        searchParams.get('refreshKey') || searchParams.get('refreshToken');
      
      if (magicToken) {
        if (isDemoHost()) {
          setHasInitiatedRedirect(true);
          navigate('/demo-login', { replace: true });
          return;
        }
        const brandId = searchParams.get('brandId');
        const branchId = searchParams.get('branchId');
        handleMagicToken(magicToken, brandId || undefined, branchId || undefined);
      } else if (refreshKey) {
        navigate(`/auth/env-handoff${location.search}`, { replace: true });
      } else {
        // Check if authentication is disabled for this environment
        const disableAuth = config.STAGE_NO_AUTH;
        
        if (disableAuth) {
          // For production bypass, set mock user directly without calling backend
          setStageMockUser();
        } else if (isDemoHost()) {
          setHasInitiatedRedirect(true);
          navigate('/demo-login', { replace: true });
        } else {
          // Add a small delay before redirecting to ensure auth state is properly checked
          const redirectTimer = setTimeout(() => {
            // Double-check authentication state before redirecting
            const currentToken = AuthService.getToken();
            if (!currentToken && !isAuthenticated) {
              setHasInitiatedRedirect(true);
              const branchId = searchParams.get('branchId');
              if (!config.STAGE_NO_AUTH) {
                AuthService.redirectToLogin(branchId || undefined);
              }
            }
          }, 500); // 500ms delay to ensure state is settled
          
          return () => clearTimeout(redirectTimer);
        }
      }
    }
  }, [isLoading, isAuthenticated, location, handleDevLogin, handleMagicToken, hasInitiatedRedirect, navigate]);

  const login = async (magicToken: string, brandId?: string, branchId?: string) => {
    await handleMagicToken(magicToken, brandId, branchId);
  };

  const logout = async () => {
    setIsLoading(true);
    await AuthService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setIsLoading(false);
    if (!config.STAGE_NO_AUTH) {
      if (isDemoHost()) {
        navigate('/demo-login', { replace: true });
      } else {
        AuthService.redirectToLogin();
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      licenses,
      login,
      logout,
      checkAuth,
      fetchLicenses
    }}>
      {children}
    </AuthContext.Provider>
  );
};
